import { InvoicePlan, InvoicePlanStatus } from '@wonderschool/common-base-types';
import { Card } from '@wonderschool/common-base-ui';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Grid, Table } from 'semantic-ui-react';
import { dateFormatter } from '../../../../helpers/dates';
import {
  getNextChargeServicePeriod,
  getProjectedCharges,
  isCadenceTransitionConfirmation,
} from '../../../../helpers/invoicePlans';
import { formatCurrency } from '../../../../helpers/utils';
import DateTime from '../../../DateTime/DateTime';
import FeeDisclaimerContainer from '../../../Shared/FeeDisclaimer/FeeDisclaimerContainer';
import InvoiceItemList from '../InvoiceItemList';

type InvoicePlanSummaryProps = {
  selectedInvoicePlan: InvoicePlan | null;
  responsibleForBilling: any;
  organization: any | null;
  showProjectedCharges?: boolean;
  timezone?: string;
};

export function InvoicePlanSummary({
  selectedInvoicePlan,
  responsibleForBilling,
  organization,
  showProjectedCharges = false,
  timezone,
}: InvoicePlanSummaryProps) {
  const { t } = useTranslation();
  const isEnded = selectedInvoicePlan?.status === InvoicePlanStatus.ENDED;

  // total/dateStart are present on the plan being confirmed but aren't all on the shared
  // InvoicePlan type; widen locally for the projection.
  const plan = selectedInvoicePlan as
    | (InvoicePlan & {
        total?: number;
        dateStart?: number;
        dateEnd?: number;
        dateDue?: number;
        unenrollmentDate?: number;
        oldBillingInterval?: string;
        intervalChangeServicePeriodPreview?: string;
      })
    | null;
  const intervalChanged = isCadenceTransitionConfirmation(plan);
  const nextChargePeriod = isEnded || intervalChanged ? null : getNextChargeServicePeriod(plan, timezone);
  const projected =
    showProjectedCharges && !(intervalChanged && plan?.dateEnd != null)
      ? getProjectedCharges({
          perChargeAmount: plan?.total ?? 0,
          billingInterval: plan?.billingInterval,
          dateStart: intervalChanged ? plan?.dateDue : plan?.dateStart,
          dateEnd: plan?.dateEnd,
        })
      : null;
  const projectedLabel =
    projected &&
    t(projected.bounded ? 'billing.invoicePlanSummary.throughDate' : 'billing.invoicePlanSummary.perYear', {
      perCharge: formatCurrency(plan?.total ?? 0),
      count: projected.count,
      total: formatCurrency(projected.total),
      endDate:
        (plan?.unenrollmentDate ?? plan?.dateEnd)
          ? dateFormatter((plan?.unenrollmentDate ?? plan?.dateEnd)!, 'MM/DD/YYYY')
          : undefined,
    });

  return (
    <Card
      title={
        <div className="flex flex-row gap-2 p-2">
          <div className="text-3xl font-bold text-black" data-testid="invoice-plan-detail-title">
            {t('billing.invoicePlan.summary.title')}
          </div>
        </div>
      }
    >
      <div className="p-4">
        <Grid columns="equal" stackable>
          <Grid.Row>
            <Grid.Column>
              <Table compact basic="very" className="billing-summary">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell data-testid="invoice-plan-detail-billed-to-label">
                      {t('billing.invoicePlan.summary.billedToLabel')}
                    </Table.Cell>
                    <Table.Cell data-testid="invoice-plan-detail-billed-to-value">
                      {responsibleForBilling.map((student) => student.displayName).join(', ')}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Grid.Column>

            <Grid.Column>
              <Table singleLine compact basic="very" className="billing-summary">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell data-testid="invoice-plan-detail-plan-number-label">
                      {t('billing.invoicePlan.summary.planNumberLabel')}
                    </Table.Cell>
                    <Table.Cell data-testid="invoice-plan-detail-plan-number-value">
                      {selectedInvoicePlan && selectedInvoicePlan?.id}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell data-testid="invoice-plan-detail-next-invoice-date-label">
                      <strong>{t('billing.invoicePlan.summary.nextInvoiceDateLabel')}</strong>
                    </Table.Cell>
                    <Table.Cell data-testid="invoice-plan-detail-next-invoice-date-value">
                      <strong>
                        {selectedInvoicePlan?.dateSend && !isEnded ? (
                          <DateTime epoch={selectedInvoicePlan.dateSend} format={'MM/DD/YYYY'} />
                        ) : (
                          'n/a'
                        )}
                      </strong>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell data-testid="invoice-plan-detail-next-due-date-label">
                      <strong>{t('billing.invoicePlan.summary.nextDueDateLabel')}</strong>
                    </Table.Cell>
                    <Table.Cell data-testid="invoice-plan-detail-next-due-date-value">
                      <strong>
                        {selectedInvoicePlan?.dateDue && !isEnded ? (
                          <DateTime epoch={selectedInvoicePlan.dateDue} format={'MM/DD/YYYY'} />
                        ) : (
                          'n/a'
                        )}
                      </strong>
                    </Table.Cell>
                  </Table.Row>
                  {nextChargePeriod ? (
                    <Table.Row>
                      <Table.Cell data-testid="invoice-plan-detail-next-charge-covers-label">
                        <strong>{t('billing.invoicePlanSummary.nextChargeCovers')}</strong>
                      </Table.Cell>
                      <Table.Cell data-testid="invoice-plan-detail-next-charge-covers-value">
                        <strong>{nextChargePeriod}</strong>
                      </Table.Cell>
                    </Table.Row>
                  ) : null}
                  {(plan?.unenrollmentDate ?? plan?.dateEnd) ? (
                    <Table.Row>
                      <Table.Cell data-testid="invoice-plan-detail-end-date-label">
                        <strong>{t('billing.invoicePlan.summary.endDateLabel')}</strong>
                      </Table.Cell>
                      <Table.Cell data-testid="invoice-plan-detail-end-date-value">
                        <strong>
                          <DateTime epoch={(plan?.unenrollmentDate ?? plan?.dateEnd)!} format={'MM/DD/YYYY'} />
                        </strong>
                      </Table.Cell>
                    </Table.Row>
                  ) : null}
                  <Table.Row>
                    <Table.Cell data-testid="invoice-plan-detail-interval-label">
                      <strong>{t('billing.invoicePlan.summary.billingIntervalLabel')}</strong>
                    </Table.Cell>
                    <Table.Cell data-testid="invoice-plan-detail-interval-value">
                      {selectedInvoicePlan && <strong>{t(selectedInvoicePlan.billingInterval)}</strong>}
                    </Table.Cell>
                  </Table.Row>
                  {projected ? (
                    <Table.Row>
                      <Table.Cell data-testid="invoice-plan-detail-projected-charges-label">
                        <strong>{t('billing.invoicePlanSummary.projectedCharges')}</strong>
                      </Table.Cell>
                      <Table.Cell data-testid="invoice-plan-detail-projected-charges-value">
                        <strong>{projectedLabel}</strong>
                      </Table.Cell>
                    </Table.Row>
                  ) : null}
                  {intervalChanged ? (
                    <Table.Row>
                      <Table.Cell data-testid="invoice-plan-detail-future-invoices-label">
                        <strong>{t('billing.invoicePlanSummary.futureInvoicesOnlyLabel')}</strong>
                      </Table.Cell>
                      <Table.Cell data-testid="invoice-plan-detail-future-invoices-value">
                        {t('billing.invoicePlanSummary.futureInvoicesOnlyDescription')}
                      </Table.Cell>
                    </Table.Row>
                  ) : null}
                  {intervalChanged && plan?.intervalChangeServicePeriodPreview ? (
                    <Table.Row>
                      <Table.Cell data-testid="invoice-plan-detail-service-period-label">
                        <strong>{t('billing.intervalChange.firstServicePeriod')}</strong>
                      </Table.Cell>
                      <Table.Cell data-testid="invoice-plan-detail-service-period-value">
                        {plan.intervalChangeServicePeriodPreview}
                      </Table.Cell>
                    </Table.Row>
                  ) : null}
                </Table.Body>
              </Table>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              {!!selectedInvoicePlan?.invoiceItemList && (
                <InvoiceItemList
                  invoiceItemList={selectedInvoicePlan.invoiceItemList}
                  isEditable={false}
                  totalOverride={intervalChanged ? plan?.total : undefined}
                />
              )}
              <Table singleLine compact basic="very" className="billing-summary">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell colSpan="4">{<FeeDisclaimerContainer currentOrganization={organization} />}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </Card>
  );
}
